import { ApplicationRef, ComponentFactoryResolver, ComponentRef, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorTemplateSideBarComponent } from '@shared/component/sidebar/error-template-side-bar.component';
import { AuditLog } from '@shared/models/audit-log.model';
import { LayoutActionVersionError } from '@shared/store/actions/layout.action';
import { LayoutState } from '@shared/store/state/layout.state';
import { BehaviorSubject, Observable, Subject, of, switchMap } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

export enum Action {
  CONFIRM = 'confirm',
  CANCEL = 'cancel',
  CLOSE = 'close'
}

export interface SidebarModel {
  title?: string;
  contentMessage?: string;
  okText?: string;
  cancelText?: string;
  hideCancelButton?: boolean;
  hideCloseButton?: boolean;
  hideOkButton?: boolean;
  icon?: string;

  // history
  headerText?: string;
  docNo?: string;
  docNos?: string;
  auditLogs?: AuditLog[];
  auditLogs$?: Observable<AuditLog[]>;
  editNameByKey?: string;
  translatePrefix?: string;
  translate?: boolean;

  //other
  template?: any;
  templateData?: any;
  isReasonSidebar?: boolean;
  defaultImage?: any;
  item?: any;
  imageSrc?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SidebarRefService {
  action$ = new Subject<Action>();
  private sidebarVisible = new BehaviorSubject<boolean>(true);
  sidebarVisible$ = this.sidebarVisible.asObservable();
  private currentSidebar: ComponentRef<any> | null = null;
  manualTrigger$ = new Subject<void>();

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    private translate: TranslateService,
    private store: Store<LayoutState>
  ) {}

  openHandleVersionError(): Observable<any> {
    return this.open(ErrorTemplateSideBarComponent, {
      title: this.translate.instant('SIDEBAR.HEADER.ERROR'),
      contentMessage: this.translate.instant('SIDEBAR.CONTENT.ERROR'),
      okText: this.translate.instant('SIDEBAR.BUTTON.ACCEPT'),
      hideCancelButton: true,
      hideCloseButton: true
    }).pipe(
      switchMap(() => of(new LayoutActionVersionError(true))),
      finalize(() => {
        this.store.dispatch(new LayoutActionVersionError(false));
      })
    );
  }

  /**
   * Creates a sidebar component and attaches it to the appRef.
   *
   * @param {T} component - The component to be instantiated.
   * @param {ConfirmSidebar} [data] - Optional data to be passed to the component.
   * @returns {Observable<Action>}
   * `Action` enumeration:
   * - `CONFIRM` is emitted when the user confirm the sidebar.
   * - `CANCEL` is emitted when the user cancel the sidebar.
   * - `CLOSE` is emitted when the sidebar is closed.
   */
  open<T>(component: T, data: SidebarModel, takeOne = true): Observable<Action> {
    if (this.currentSidebar) {
      this.close();
    }
    this.createSidebar(component, data);
    this.sidebarVisible.next(true);

    let observable$ = this.action$.asObservable();

    if (takeOne) {
      observable$ = observable$.pipe(take(1));
    } else {
      observable$ = this.action$.asObservable().pipe(takeUntil(this.manualTrigger$));
    }
    return observable$;
  }

  close() {
    if (this.currentSidebar) {
      this.appRef.detachView(this.currentSidebar.hostView);
      this.currentSidebar.destroy();
      this.currentSidebar = null;
    }
    this.sidebarVisible.next(false);
  }

  private reasonSubject = new Subject<string>();

  getReason(): Observable<string> {
    return this.reasonSubject.asObservable();
  }

  setReason(reason: string) {
    this.reasonSubject.next(reason);
  }

  emitAction(action: Action) {
    this.action$.next(action);
  }

  private createSidebar(component: any, data?: any) {
    const factory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = factory.create(this.injector);

    // Pass data to the component instance if it has a 'data' property
    if (data && componentRef.instance['data'] !== undefined) {
      componentRef.instance['data'] = data;
    }

    this.appRef.attachView(componentRef.hostView);
    document.body.appendChild(componentRef.location.nativeElement);

    this.currentSidebar = componentRef;
  }
}

// Example 1
// NormalTemplateSideBarComponent, SuccessTemplateSideBarComponent, ErrorTemplateSideBarComponent

// this.sideBarRefService.open(NormalTemplateSideBarComponent, {
//   title: "SIDEBAR.HEADER.CONFIRM",
//   okText: "SIDEBAR.BUTTON.CONFIRM",
//   cancelText: "SIDEBAR.BUTTON.CANCEL",
//   contentMessage: "คุณต้องการยืนยันทำรายการนี้หรือไม่?"
// }).subscribe({
//   next: (res) => {
//     if (res === Action.CONFIRM) {
//         this.sideBarRefService.close();
//     }
//   }
// });

// Example 2
// openSideBar1() {
//   this.sideBarRefService.open(NormalTemplateSideBarComponent, {
//     title: "SIDEBAR.HEADER.CONFIRM",
//     contentMessage: "คุณต้องการยืนยันทำรายการนี้หรือไม่?"
//   }).pipe(
//     switchMap(res => {
//       if (res === Action.CONFIRM) {
//         return this.sideBarRefService.open(SuccessTemplateSideBarComponent, {
//           title: "ยืนยันการทำรายการ",
//           okText: "ยืนยัน",
//           cancelText: "ยกเลิก",
//           contentMessage: "คุณต้องการยืนยันทำรายการนี้หรือไม่?"
//         });
//       }
//       return EMPTY;
//     })
//   ).subscribe({
//     next: (res) => {
//       if (res === Action.CONFIRM) {
//         setTimeout(() => {
//           this.sideBarRefService.close();
//         }, 2000);
//       }
//     }
//   });
// }
